import React, { useEffect, useState, useRef } from "react";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import "../../Merchant/EditProfileForm/editprofileform.scss";
import BreadCrumsHeader from "../../common/BreadCrums";
import FormLayout from "../../common/FormLayout";
import PersonIcon from "../../../Assets/Images/Person.svg";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import {
  updatePasswordSchema,
  editProfileSchema,
  fundraiserEditProfileSchema,
} from "../../../utils/validationSchemas";
import { setUserData } from "../../../redux/slices/authSlice";
import Loader from "../../common/Loader";
import EditProfile from "./EditProfile";
import {
  getFundraiserProfile,
  updateFundraiserProfile,
  getFundraiserCategories,
  getSubCategoriesByCategoryId,
} from "../../../services/fundraiserService";
import { getReasons } from "../../../services/onboardingService";
import { inferCountryFromPhoneNumber, DEFAULT_COUNTRY } from "../../../utils/phoneValidation";
import UpdatePassword from "../../Merchant/EditProfileForm/UpdatePassword";
import useProfilePasswordUpdate from "../../common/ResetPassword/useProfilePasswordUpdate";
import BankAccountDetails from "../../Fundraiser/BanksList/BankAccountDetails";
import toast from "../../../utils/toast";
import { broadcastFundraiserProfileLogoSync } from "../../../utils/fundraiserProfileSync";

const getFundraiserLogo = (fundraiser) =>
  fundraiser?.logo ||
  fundraiser?.fundraiserLogo ||
  fundraiser?.logoUrl ||
  fundraiser?.image ||
  fundraiser?.profileImage ||
  fundraiser?.user?.logo ||
  fundraiser?.user?.image ||
  fundraiser?.user?.profileImage ||
  "";

const FundraiserEditProfileFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.franchiseReducer?.loading);
  const [uploadClicked, setUploadClicked] = useState(false);
  const [profileData, setProfileData] = useState();
  const [isUpdatePassword, setIsUpdatePassword] = useState(false);
  const {
    confirmationModal: passwordConfirmationModal,
    submitPasswordUpdate,
  } = useProfilePasswordUpdate();
  const authTokenData = useSelector((state) => state?.authReducer?.tokenData);
  const userData = useSelector((state) => state.authReducer.tokenData.user);
  const { id = null } = location?.state || "";

  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const format = "hh:mm A";

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [allReasons, setAllReasons] = useState([]);
  const [customReasonId, setCustomReasonId] = useState(null);
  const lastSubmittedRef = useRef({});
  const previousCategoryRef = useRef(null);
  const hasNormalizedSubsRef = useRef(false);
  const initialSubCategoryIdsRef = useRef([]);

  const stripHtml = (html) => {
    if (!html || typeof html !== "string") return html;
    return html
      .replace(/<[^>]*>/g, "") // remove tags
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .trim();
  };

  useEffect(() => {
    dispatch(getFundraiserCategories())
      .unwrap()
      .then((res) => {
        if (res?.success && res?.data) setCategories(res.data);
      });
    dispatch(getReasons())
      .unwrap()
      .then((res) => {
        if (res?.success && res?.data) setAllReasons(res.data);
      });
  }, [dispatch]);

  useEffect(() => {
    if (
      profileData &&
      profileData.category &&
      profileData.category.length > 0
    ) {
      const selectedCat = profileData.category[0];
      if (selectedCat?._id) {
        // Initialize previous category on first profile load
        if (!previousCategoryRef.current) {
          previousCategoryRef.current = selectedCat._id;
        }
        dispatch(getSubCategoriesByCategoryId({ id: selectedCat._id }))
          .unwrap()
          .then((res) => {
            if (res?.success) {
              const apiSubs = Array.isArray(res?.data) ? res.data : [];
              if (apiSubs.length > 0) {
                setSubCategories(apiSubs);
              } else {
                // Fallback: if API returns empty (e.g., Sporting Clubs), use existing profile subCategory only
                const fallback = Array.isArray(
                  profileData?.subCategory || profileData?.subCategories,
                )
                  ? (profileData?.subCategory || profileData?.subCategories)
                      .map((s) => ({
                        _id: s?._id || s,
                        title: s?.title || s?.name || s,
                      }))
                      .filter((s) => s._id && s.title)
                  : [];
                setSubCategories(fallback);
              }
            }
          });
      }
    }
  }, [dispatch, profileData]);

  const getCategoryIdByTitle = (title) => {
    if (!title || (typeof title === "string" && title.trim() === ""))
      return null;
    const cat = categories.find((c) => c.title === title || c._id === title);
    return cat
      ? cat._id
      : typeof title === "string" &&
          title.length === 24 &&
          /^[0-9a-fA-F]+$/.test(title)
        ? title
        : null;
  };

  const getSubCategoryIdsByTitles = (titles) => {
    return (titles || [])
      .map((title) => {
        if (
          typeof title === "string" &&
          title.length === 24 &&
          /^[0-9a-fA-F]+$/.test(title)
        ) {
          return title;
        }
        const sub = subCategories.find(
          (s) => s.title === title || s._id === title,
        );
        return sub ? sub._id : null;
      })
      .filter(
        (id) =>
          typeof id === "string" &&
          id.length === 24 &&
          /^[0-9a-fA-F]+$/.test(id),
      );
  };

  const getReasonObjectsByStrings = (reasons) => {
    return (reasons || [])
      .map((reason) => {
        if (
          typeof reason === "string" &&
          reason.length === 24 &&
          /^[0-9a-fA-F]+$/.test(reason)
        ) {
          return { _id: reason };
        }
        const found = allReasons.find(
          (r) => r.reason === reason || r._id === reason,
        );
        return found ? { _id: found._id } : null;
      })
      .filter(Boolean);
  };

  const initialValues = isUpdatePassword
    ? {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }
    : {
        logo: getFundraiserLogo(profileData),
        businessName:
          profileData?.fundraiserName ||
          profileData?.user?.name ||
          profileData?.name ||
          "",
        email: profileData?.user?.email || "",
        contact: profileData?.contactPersonNumber || "",
        contactCountry: inferCountryFromPhoneNumber(profileData?.contactPersonNumber),
        address: profileData?.address || "",
        websiteUrl: profileData?.website || "",
        personContact: profileData?.contactPersonName || "",
        aboutBusiness: stripHtml(
          profileData?.aboutUs || profileData?.description || "",
        ),
        fundraiserCause: profileData?.reason
          ? profileData.reason.map((r) => {
              // Prefer IDs for select values; fallback to string
              if (
                typeof r === "string" &&
                r.length === 24 &&
                /^[0-9a-fA-F]+$/.test(r)
              ) {
                return r;
              }
              return r?._id || (typeof r === "string" ? r : r?.reason);
            })
          : [],
        fundraiserCategory: (() => {
          const cats = profileData?.category || profileData?.categories;
          if (Array.isArray(cats) && cats.length > 0) {
            const cat = cats[0];
            if (typeof cat === "string") return cat;
            return cat?.title || cat?._id || "";
          }
          return typeof cats === "string" ? cats : "";
        })(),
        fundraiserSubCategory:
          (profileData?.subCategory || profileData?.subCategories) &&
          Array.isArray(profileData?.subCategory || profileData?.subCategories)
            ? (profileData?.subCategory || profileData?.subCategories).map(
                (sub) => {
                  let v;
                  if (
                    typeof sub === "string" &&
                    sub.length === 24 &&
                    /^[0-9a-fA-F]+$/.test(sub)
                  ) {
                    v = sub;
                  } else {
                    v = sub?._id || sub?.title || "";
                  }
                  return String(v);
                },
              )
            : [],
        organisationName: profileData?.organizationName || "",
        abn: profileData?.abnNumber || "",
        charityRegistrationNumber: profileData?.charityRegistrationNumber || "",
        accountsEmail: profileData?.accountEmail || "",
      };

  const fundraiserSchema = fundraiserEditProfileSchema.shape({
    businessName: Yup.string()
      .required("Fundraiser Name is required")
      .max(40, "Fundraiser Name cannot be more than 40 characters")
      .matches(
        /^[\p{L}0-9\s&'().,\-@#$%!?/:;+=_*[\]{}|~`"\\]+$/u,
        "Fundraiser Name can contain letters, numbers, spaces, and special characters",
      ),
    organisationName: Yup.string()
      .max(100, "Organisation Name cannot be more than 100 characters")
      .optional(),
    address: Yup.string().required("Fundraiser Address is required"),
    websiteUrl: Yup.string()
      .required("Website URL is required")
      .matches(
        /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}(\/[^\s]*)?$/,
        "Invalid website URL",
      ),
    aboutBusiness: Yup.string()
      .required("About Business is required")
      .max(500, "About Business cannot be more than 500 characters"),
    fundraiserCategory: Yup.string().required(
      "Fundraiser Category is required",
    ),
    fundraiserSubCategory: Yup.array().when("fundraiserCategory", {
      is: (val) => val && val !== "Community Organisations",
      then: (s) => s.min(1, "Please select at least one sub category"),
      otherwise: (s) => s.optional(),
    }),
    fundraiserCause: Yup.array().min(1, "Please select at least one cause"),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: isUpdatePassword
      ? updatePasswordSchema
      : userData?.role === "fundraiser"
        ? fundraiserSchema
        : editProfileSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      try {
        if (isUpdatePassword) {
          const passwordPayload = {
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
            confirmPassword: values.confirmPassword,
          };
          await submitPasswordUpdate(passwordPayload, () => {
            setIsUpdatePassword(false);
            formik.setValues({
              currentPassword: "",
              newPassword: "",
              confirmPassword: "",
            });
            formik.setTouched({});
          });
          return;
        }

        if (
          values.fundraiserCause &&
          values.fundraiserCause.includes("Other")
        ) {
          toast.error(
            "Please create and select the custom cause before saving.",
          );
          return;
        }

        const payload = {};
        const addIfTruthy = (key, val) => {
          if (val === undefined || val === null) return;
          if (typeof val === "string") {
            const trimmed = val.trim();
            if (trimmed === "") return;
            payload[key] = trimmed;
            return;
          }
          if (Array.isArray(val) && val.length === 0) return;
          payload[key] = val;
        };

        addIfTruthy("fundraiserName", values.businessName);
        addIfTruthy("description", values.aboutBusiness);
        addIfTruthy("aboutUs", values.aboutBusiness);
        addIfTruthy("logo", values.logo);
        addIfTruthy("website", values.websiteUrl);
        addIfTruthy("contactPersonName", values.personContact);
        addIfTruthy("contactPersonNumber", values.contact);
        addIfTruthy("contactPersonCountry", values.contactCountry || DEFAULT_COUNTRY);
        addIfTruthy("address", values.address);

        const resolvedCategoryId = getCategoryIdByTitle(
          values.fundraiserCategory,
        );
        if (resolvedCategoryId) {
          payload.category = [resolvedCategoryId];
        }

        if (values.fundraiserCategory !== "Community Organisations") {
          const resolvedSubIds = getSubCategoryIdsByTitles(
            values.fundraiserSubCategory,
          );
          if (resolvedSubIds && resolvedSubIds.length > 0) {
            payload.subCategory = resolvedSubIds;
          }
        } else {
          // Explicitly clear sub categories if category demands it
          payload.subCategory = [];
        }

        const resolvedReasons = (() => {
          const reasonObjects = getReasonObjectsByStrings(
            values.fundraiserCause,
          );
          if (
            customReasonId &&
            !reasonObjects.some((r) => r._id === customReasonId)
          ) {
            reasonObjects.push({ _id: customReasonId });
          }
          return reasonObjects.filter((r) => r && r._id);
        })();
        if (resolvedReasons && resolvedReasons.length > 0) {
          payload.reason = resolvedReasons;
        }

        addIfTruthy(
          "organizationName",
          values.businessName ? values.businessName.trim() : undefined,
        );
        addIfTruthy("abnNumber", values.abn);
        addIfTruthy(
          "charityRegistrationNumber",
          values.charityRegistrationNumber,
        );
        addIfTruthy("accountEmail", values.accountsEmail);
        // Persist logo updates including explicit clear (empty string)
        if (values.logo === "") {
          payload.logo = ""; // explicit clear request
        } else {
          addIfTruthy("logo", values.logo);
        }
        // Keep a snapshot of values we send to prevent UI from clearing if API returns null
        lastSubmittedRef.current = {
          ...lastSubmittedRef.current,
          accountEmail: values.accountsEmail
            ? values.accountsEmail.trim()
            : undefined,
        };

        console.warn("[Debug] Update Profile Payload:", payload);

        const result = await updateFundraiserProfile(payload);
        console.log("API response:", result);

        if (result?.success) {
          toast.success("Profile updated successfully");
          const finalFundraiserName =
            values.businessName?.trim() ||
            result?.data?.fundraiserName ||
            result?.data?.user?.name ||
            "";
          // Keep a snapshot for fields the API may return stale immediately after write
          lastSubmittedRef.current = {
            ...lastSubmittedRef.current,
            accountEmail: values.accountsEmail
              ? values.accountsEmail.trim()
              : undefined,
            charityRegistrationNumber: values.charityRegistrationNumber
              ? values.charityRegistrationNumber.trim()
              : undefined,
            fundraiserName: finalFundraiserName || undefined,
          };

          if (authTokenData?.user) {
            dispatch(
              setUserData({
                ...authTokenData,
                user: {
                  ...authTokenData.user,
                  name: finalFundraiserName || authTokenData.user?.name || "",
                },
              }),
            );
          }

          // Use the data returned by the API immediately to ensure UI is in sync
          // This avoids "reverts" caused by re-fetching too quickly (lag)
          if (result?.data) {
            // Update profile data first
            setProfileData(result.data);

            // Convert subcategory IDs back to titles for form display
            const normalizedSubCategories =
              result.data.subCategory?.map((sub) => {
                if (typeof sub === "object" && sub._id && sub.title) {
                  return sub.title; // Use the title for form display
                } else if (typeof sub === "string") {
                  if (/^[0-9a-fA-F]{24}$/.test(sub)) {
                    // It's an ID, find the corresponding title
                    const found = subCategories.find((s) => s._id === sub);
                    return found ? found.title : sub;
                  }
                  return sub; // Already a title
                }
                return sub;
              }) || [];

            // Update formik values with normalized subcategories after a brief delay
            // to ensure the profileData useEffect doesn't override our values
            setTimeout(() => {
              setFieldValue("fundraiserSubCategory", normalizedSubCategories);
            }, 100);
          } else {
            // Fallback to manual sync if data not returned
            setProfileData((prev) => ({
              ...(prev || {}),
              accountEmail:
                lastSubmittedRef.current.accountEmail ??
                prev?.accountEmail ??
                "",
              charityRegistrationNumber:
                lastSubmittedRef.current.charityRegistrationNumber ??
                prev?.charityRegistrationNumber ??
                "",
              fundraiserName:
                lastSubmittedRef.current.fundraiserName ??
                prev?.fundraiserName ??
                "",
              description: values.aboutBusiness,
              aboutUs: values.aboutBusiness,
              logo: values.logo === "" ? "" : values.logo || prev?.logo || "",
              category: values.fundraiserCategory
                ? [
                    {
                      title: values.fundraiserCategory,
                      _id: resolvedCategoryId,
                    },
                  ]
                : prev?.category || [],
              subCategory: values.fundraiserSubCategory
                ? values.fundraiserSubCategory.map((val) => {
                    if (
                      typeof val === "string" &&
                      /^[0-9a-fA-F]{24}$/.test(val)
                    ) {
                      const found = subCategories.find((s) => s._id === val);
                      return found
                        ? { _id: found._id, title: found.title }
                        : { _id: val, title: val };
                    }
                    const found = subCategories.find(
                      (s) => s.title === val || s._id === val,
                    );
                    return found
                      ? { _id: found._id, title: found.title }
                      : { _id: val, title: val };
                  })
                : prev?.subCategory || [],
              user: {
                ...(prev?.user || {}),
                name:
                  lastSubmittedRef.current.fundraiserName ??
                  prev?.user?.name ??
                  "",
              },
            }));
          }

          setCustomReasonId(null);

          // Trigger shared fundraiser-logo refresh across the app and other tabs
          broadcastFundraiserProfileLogoSync(
            values.logo === "" ? "" : values.logo || "",
          );
        } else {
          toast.error(result?.message || "Failed to update profile");
        }
      } catch (error) {
        // console.error("Profile update error:", error); // Removed debug log
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to update profile",
        );
      }
    },
  });

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
    setFieldValue,
    setValues,
    resetForm,
  } = formik;

  useEffect(() => {
    if (formik?.values?.fundraiserCategory && profileData) {
      const categoryId = getCategoryIdByTitle(formik.values.fundraiserCategory);
      if (categoryId && categoryId.length === 24) {
        // Clear subcategories only if the user actually changed the category
        if (
          previousCategoryRef.current &&
          previousCategoryRef.current !== categoryId
        ) {
          setFieldValue("fundraiserSubCategory", []);
        }
        let isMounted = true;
        dispatch(getSubCategoriesByCategoryId({ id: categoryId }))
          .unwrap()
          .then((res) => {
            if (isMounted && res?.success) {
              const apiSubs = Array.isArray(res?.data) ? res.data : [];
              if (apiSubs.length > 0) {
                setSubCategories(apiSubs);
              } else {
                const fallback = Array.isArray(
                  profileData?.subCategory || profileData?.subCategories,
                )
                  ? (profileData?.subCategory || profileData?.subCategories)
                      .map((s) => ({
                        _id: s?._id || s,
                        title: s?.title || s?.name || s,
                      }))
                      .filter((s) => s._id && s.title)
                  : [];
                setSubCategories(fallback);
              }
              // Update the previous category after we load the new subcategories
              previousCategoryRef.current = categoryId;
            }
          })
          .catch(() => {});
        return () => {
          isMounted = false;
        };
      }
    }
  }, [formik?.values?.fundraiserCategory]);

  // Normalize sub-category IDs to titles for display once subCategories are loaded (only once, prevent loops)
  useEffect(() => {
    if (
      subCategories.length > 0 &&
      initialSubCategoryIdsRef.current.length > 0 &&
      !hasNormalizedSubsRef.current
    ) {
      // Convert stored IDs to titles
      const normalized = initialSubCategoryIdsRef.current
        .map((id) => {
          const found = subCategories.find((s) => s._id === id);
          return found ? found.title : id;
        })
        .filter((t) => typeof t === "string" && t.length > 0);

      if (normalized.length > 0) {
        hasNormalizedSubsRef.current = true;
        resetForm({
          values: { ...formik.values, fundraiserSubCategory: normalized },
        });
        // Clear stored IDs after normalization
        initialSubCategoryIdsRef.current = [];
      } else {
        hasNormalizedSubsRef.current = true;
      }
    } else if (
      subCategories.length > 0 &&
      initialSubCategoryIdsRef.current.length === 0
    ) {
      // No IDs to normalize, mark as done
      hasNormalizedSubsRef.current = true;
    }
  }, [subCategories, resetForm, formik.values]);

  // Reset normalization flag and stored IDs when profileData changes
  useEffect(() => {
    hasNormalizedSubsRef.current = false;
    initialSubCategoryIdsRef.current = [];
  }, [profileData]);

  // Removed duplicate clearing effect; handled in the fetch effect above

  useEffect(() => {
    if (profileData) {
      const finalEmail =
        lastSubmittedRef.current.accountEmail !== undefined
          ? lastSubmittedRef.current.accountEmail
          : profileData?.accountEmail || "";
      const finalCharity =
        lastSubmittedRef.current.charityRegistrationNumber !== undefined
          ? lastSubmittedRef.current.charityRegistrationNumber
          : profileData?.charityRegistrationNumber || "";
      const finalFundraiserName =
        lastSubmittedRef.current.fundraiserName !== undefined
          ? lastSubmittedRef.current.fundraiserName
          : profileData?.fundraiserName || profileData?.user?.name || "";

      // Get the current fundraiserSubCategory value to preserve it if we just saved
      const currentSubCategoryValue = formik.values.fundraiserSubCategory;

      console.log("Setting form values with accountEmail:", {
        profileDataEmail: profileData?.accountEmail,
        lastSubmittedEmail: lastSubmittedRef.current.accountEmail,
        finalEmail,
      });

      resetForm({
        values: {
          logo: getFundraiserLogo(profileData),
          businessName: finalFundraiserName,
          email: profileData?.user?.email || "",
          contact: profileData?.contactPersonNumber || "",
          contactCountry: inferCountryFromPhoneNumber(profileData?.contactPersonNumber),
          address: profileData?.address || "",
          websiteUrl: profileData?.website || "",
          personContact: profileData?.contactPersonName || "",
          aboutBusiness: stripHtml(
            profileData?.aboutUs || profileData?.description || "",
          ),
          fundraiserCause: profileData?.reason
            ? profileData.reason.map((r) => {
                if (
                  typeof r === "string" &&
                  r.length === 24 &&
                  /^[0-9a-fA-F]+$/.test(r)
                ) {
                  return r;
                }
                return typeof r === "string" ? r : r.reason || r._id;
              })
            : [],
          fundraiserCategory: (() => {
            const cats = profileData?.category || profileData?.categories;
            if (Array.isArray(cats) && cats.length > 0) {
              const cat = cats[0];
              if (typeof cat === "string") return cat;
              return cat?.title || cat?._id || "";
            }
            return typeof cats === "string" ? cats : "";
          })(),
          // Preserve the current fundraiserSubCategory value if we just saved (to maintain normalized titles)
          fundraiserSubCategory:
            currentSubCategoryValue ||
            (() => {
              const subs = Array.isArray(
                profileData?.subCategory || profileData?.subCategories,
              )
                ? (profileData?.subCategory || profileData?.subCategories)
                    .map((sub) => {
                      if (typeof sub === "string") {
                        if (/^[0-9a-fA-F]{24}$/.test(sub)) return sub;
                        return sub;
                      }
                      return sub?._id || sub?.title || "";
                    })
                    .filter(Boolean)
                : [];
              // Store IDs for later normalization
              initialSubCategoryIdsRef.current = subs.filter(
                (s) => typeof s === "string" && /^[0-9a-fA-F]{24}$/.test(s),
              );
              return subs;
            })(),
          organisationName: profileData?.organizationName || "",
          abn: profileData?.abnNumber || "",
          charityRegistrationNumber: finalCharity,
          accountsEmail: finalEmail,
        },
      });
    }
  }, [profileData, resetForm]);

  const items = [{ title: "Profile" }, { title: "Edit Profile" }];

  const [placeId, setPlaceId] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const handleCancelClick = () => {
    resetForm();
    setCustomReasonId(null);
    setResetKey((prev) => prev + 1);
  };

  const getProfile = async () => {
    try {
      const fundraiserProfile = await getFundraiserProfile();
      if (fundraiserProfile?.success === true) {
        setProfileData(fundraiserProfile?.data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch fundraiser profile", error);
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  const handleUpdateClick = () => {
    setIsUpdatePassword(true);
  };

  const handleOtherInfoClick = () => {
    setIsUpdatePassword(false);
  };

  const handleCustomReasonCreated = (reasonId) => {
    setCustomReasonId(reasonId);
  };

  const scrollToFirstError = (formErrors) => {
    const priority = [
      "fundraiserCategory",
      "fundraiserSubCategory",
      "fundraiserCause",
      "businessName",
      "websiteUrl",
      "contact",
      "address",
      "aboutBusiness",
      "logo",
      "personContact",
      "email",
      "organisationName",
      "abn",
      "charityRegistrationNumber",
      "accountsEmail",
    ];
    let targetField = priority.find(
      (key) =>
        formErrors && Object.prototype.hasOwnProperty.call(formErrors, key),
    );
    if (!targetField) {
      const keys = Object.keys(formErrors || {});
      targetField = keys.length ? keys[0] : null;
    }
    if (!targetField) return;
    setTimeout(() => {
      try {
        const el =
          document.querySelector(`[data-field="${targetField}"]`) ||
          document.querySelector(`[name="${targetField}"]`);
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } catch (_) {}
    }, 0);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors || {}).length > 0) {
      formik.setTouched(
        Object.keys(validationErrors).reduce((acc, k) => {
          acc[k] = true;
          return acc;
        }, {}),
        true,
      );
      scrollToFirstError(validationErrors);
      const firstErrorKey = Object.keys(validationErrors)[0];
      const firstErrorMessage = firstErrorKey
        ? validationErrors[firstErrorKey]
        : null;
      if (firstErrorMessage) {
        toast.error(firstErrorMessage);
      }
      return;
    }
    handleSubmit(e);
  };

  return (
    <div className="edit-profile-container">
      {passwordConfirmationModal}
      {loading === true && <Loader />}
      {/* <BreadCrumsHeader heading="Edit Profile" items={items} form={true} /> */}
      <FormLayout
        title={"Profile Information"}
        content={
          <form onSubmit={handleFormSubmit} className="px-5">
            <div className="mt-5 mb-5 w-full flex justify-between items-center px-0 md:px-2">
              {isUpdatePassword ? (
                <p
                  className="cursor-pointer font-poppins font-[400] text-[14px] text-[#00ADEF] leading-[18px]"
                  onClick={handleOtherInfoClick}
                >
                  Update Profile Information
                </p>
              ) : (
                <p
                  className="cursor-pointer font-poppins font-[400] text-[14px] text-[#00ADEF] leading-[18px]"
                  onClick={handleUpdateClick}
                >
                  Update Password
                </p>
              )}
            </div>
            {isUpdatePassword ? (
              <UpdatePassword
                values={formik.values}
                errors={errors}
                touched={touched}
                handleBlur={handleBlur}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                setFieldTouched={formik.setFieldTouched}
              />
            ) : (
              <>
                <EditProfile
                  values={formik.values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  setFieldTouched={formik.setFieldTouched}
                  setPlaceId={setPlaceId}
                  setLogoLoading={setLogoLoading}
                  logoLoading={logoLoading}
                  resetKey={resetKey}
                  onCustomReasonCreated={handleCustomReasonCreated}
                />
                {/* Bank Account Details Section: Only show for fundraiser role */}
                {userData?.role === "fundraiser" && <BankAccountDetails />}
              </>
            )}
            <div className="col-span-12 mt-14 flex flex-col-reverse md:flex-row justify-end gap-4">
              <button
                className="w-full md:w-[30%] lg:w-[15%] h-[45px] rounded-[5px] bg-[#F0F0F0] text-[16px] font-[500] text-[#8C8C8C] border border-[#C4C4C4]"
                onClick={handleCancelClick}
                type="button"
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`w-full md:w-[30%] lg:w-[15%] h-[45px] rounded-[5px] text-[16px] font-[500] transition-all ${
                  logoLoading || formik.isSubmitting
                    ? "bg-[#E0E0E0] text-[#8C8C8C] cursor-not-allowed border border-[#C4C4C4]"
                    : "bg-[#00ADEF] text-[#FFFFFF]"
                }`}
                disabled={logoLoading || formik.isSubmitting}
              >
                Save
              </button>
            </div>
          </form>
        }
      />
    </div>
  );
};

export default FundraiserEditProfileFormPage;
